import type {
  MonitoringControlRecord,
  MonitoringControlRow,
  MonitoringStatus,
} from './monitoring-types';

/** Section id for controls the runtime reports but no imported bundle claims. */
export const UNSECTIONED_ID = '__unsectioned__';

/**
 * A section of the policy, as the left nav renders it.
 *
 * "Section" is a CONTROL BUNDLE, not a derived grouping. A policy already owns
 * `controlBundleList` and each bundle already owns its controls, so a reader
 * sees the author's own structure — not a shape invented here by splitting
 * citation strings.
 */
export interface MonitoringSection {
  id: string;
  name: string;
  /** Controls in this section. */
  total: number;
  /**
   * Controls consulted at least once in the window. NOT a pass count — a
   * consulted control can still sit at 'elevated'. It answers "is this section
   * being exercised at all", which is the coverage question.
   */
  covered: number;
  /** Highest attention level present in the section, for the rail dot. */
  attention: 'none' | 'watch' | 'elevated';
}

export interface MonitoringControlEntry {
  controlId: string;
  controlName: string;
  description: string;
  question?: string;
  record: MonitoringControlRecord;
  activity: MonitoringControlRow['activity'];
  attestation: MonitoringControlRow['attestation'];
  /** The control bundle this control was authored in. */
  bundleId: string;
  bundleName: string;
}

/**
 * Structural shape of the joined policy. Declared here rather than imported
 * from the generated client so this module stays loadable in a jest graph that
 * cannot babel-transform the generated Apollo file.
 */
export interface MonitoringPolicyLike {
  controlBundleList?:
    | ({
        _id: string;
        name: string;
        controls?:
          | ({
              identifier: string;
              name: string;
              description?: string | null;
              question?: string | null;
            } | null)[]
          | null;
      } | null)[]
    | null;
}

/**
 * Every control identifier the POLICY declares, across all its bundles.
 *
 * This is the left-hand side of the monitoring join, and it is what
 * `validateMonitoringJoin` must be given. Handing it the snapshot's own ids
 * instead would make the check vacuous — it would be comparing the runtime
 * against itself and could never report a miss.
 */
export function collectPolicyControlIds(
  policy: MonitoringPolicyLike | null | undefined
): string[] {
  const ids: string[] = [];
  for (const bundle of policy?.controlBundleList ?? []) {
    for (const ctrl of bundle?.controls ?? []) {
      if (ctrl) {
        ids.push(ctrl.identifier);
      }
    }
  }
  return ids;
}

/**
 * Join the runtime snapshot to the authored policy text.
 *
 * SHARED BY THE PAGE AND THE LEFT NAV ON PURPOSE. Both need the same controls
 * grouped the same way; two copies of this walk would drift, and the first
 * symptom would be a sidebar fraction that disagrees with the page beside it —
 * which reads as a data bug rather than a duplication bug.
 */
export function buildControlEntries(
  policy: MonitoringPolicyLike | null | undefined,
  snapshot: MonitoringStatus | null | undefined
): MonitoringControlEntry[] {
  if (!policy || !snapshot) {
    return [];
  }

  const controlMap = new Map<
    string,
    {
      name: string;
      description: string;
      question?: string;
      bundleId: string;
      bundleName: string;
    }
  >();

  for (const bundle of policy.controlBundleList ?? []) {
    if (!bundle) {
      continue;
    }
    for (const ctrl of bundle.controls ?? []) {
      if (!ctrl) {
        continue;
      }
      controlMap.set(ctrl.identifier, {
        name: ctrl.name,
        description: ctrl.description ?? '',
        question: ctrl.question ?? undefined,
        bundleId: bundle._id,
        bundleName: bundle.name,
      });
    }
  }

  return snapshot.controls.map((rec) => {
    const ctrl = controlMap.get(rec.controlId);
    // displayName (future re-bake) -> joined policy name -> snapshot record name
    const controlName = rec.displayName ?? ctrl?.name ?? rec.name;
    return {
      controlId: rec.controlId,
      controlName,
      description: ctrl?.description ?? '',
      question: ctrl?.question,
      record: rec,
      activity: rec.activity,
      attestation: rec.attestation,
      // A control present at runtime but absent from every bundle has no
      // authored home. It gets its own section rather than being dropped —
      // silently hiding it would make the coverage fraction a lie.
      bundleId: ctrl?.bundleId ?? UNSECTIONED_ID,
      bundleName: ctrl?.bundleName ?? 'Not in an imported bundle',
    };
  });
}

/**
 * Group entries into sections, preserving the order controls arrive in.
 *
 * Counts are over EVERY control in the policy, never a filtered subset. The
 * sidebar is mounted outside the page and cannot see its search/filter state,
 * and a fraction that silently tracked a filter the reader cannot see from the
 * sidebar would be worse than one that plainly describes the whole policy.
 * This matches the assessment nav, whose bundle counts are likewise unfiltered
 * while the in-page status chips narrow the list.
 */
export function buildSections(
  entries: MonitoringControlEntry[]
): MonitoringSection[] {
  const byId = new Map<string, MonitoringSection>();

  for (const e of entries) {
    const covered = e.record.consults > 0 ? 1 : 0;
    const attention =
      e.record.status === 'elevated'
        ? 'elevated'
        : e.record.status === 'watch'
          ? 'watch'
          : 'none';

    const existing = byId.get(e.bundleId);
    if (!existing) {
      byId.set(e.bundleId, {
        id: e.bundleId,
        name: e.bundleName,
        total: 1,
        covered,
        attention,
      });
      continue;
    }

    existing.total += 1;
    existing.covered += covered;
    // Highest attention wins: elevated outranks watch outranks none.
    if (
      attention === 'elevated' ||
      (attention === 'watch' && existing.attention === 'none')
    ) {
      existing.attention = attention;
    }
  }

  return Array.from(byId.values());
}
